import React, { useState } from 'react';
import { FiClipboard, FiAlertCircle, FiHome, FiImage, FiClock, FiX } from 'react-icons/fi';

const WIDE_FIELDS = ['description', 'notes', 'additionalServices', 'article'];

function PhotoThumbs({ photos, onOpen }) {
  if (!photos?.length) return null;
  return (
    <div className="flex flex-wrap gap-2 mt-2">
      {photos.map((url, index) => (
        <button
          key={`${url}-${index}`}
          type="button"
          onClick={() => onOpen(url)}
          className="w-20 h-20 rounded-lg overflow-hidden border border-gray-600 hover:border-yellow-500 transition-colors bg-gray-900"
        >
          <img src={url} alt="" loading="lazy" className="w-full h-full object-cover" />
        </button>
      ))}
    </div>
  );
}

/**
 * Affiche les champs saisis dans le formulaire de demande de mission.
 */
export default function MissionRequestFormSection({ missionForm }) {
  const [previewUrl, setPreviewUrl] = useState(null);
  const fields = missionForm?.fields || [];
  const visibleFields = fields.filter((f) => f.display);
  const rooms = missionForm?.rooms || [];
  const articlePhotos = missionForm?.articlePhotos || [];
  const history = missionForm?.history || [];
  const isEmpty = visibleFields.length === 0 && rooms.length === 0 && articlePhotos.length === 0;

  return (
    <div className="mb-8 pt-8 border-t border-gray-700">
      <div className="flex items-center mb-6 pb-4 border-b border-gray-700">
        <div className="w-10 h-10 bg-yellow-600/20 border border-yellow-600/50 rounded-lg flex items-center justify-center mr-3">
          <FiClipboard className="text-yellow-400" />
        </div>
        <div>
          <h3 className="text-xl font-bold text-gray-100">
            Données du formulaire de demande
          </h3>
          <p className="text-sm text-gray-400 mt-0.5">
            Informations saisies par le client lors de la création de la mission
          </p>
        </div>
      </div>

      {isEmpty ? (
        <div className="rounded-xl border border-dashed border-gray-600 bg-gray-900/40 p-6 flex items-start gap-3 text-gray-400">
          <FiAlertCircle className="w-5 h-5 shrink-0 mt-0.5 text-amber-400" />
          <p className="text-sm">
            Aucune donnée détaillée du formulaire n&apos;a été enregistrée pour cette mission
            (seules les adresses et dates sont disponibles).
          </p>
        </div>
      ) : (
        <div className="space-y-6">
          {visibleFields.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {visibleFields.map((field) => (
                <div key={field.key} className={WIDE_FIELDS.includes(field.key) ? 'md:col-span-2' : ''}>
                  <label className="block text-sm font-semibold text-gray-300 mb-2">{field.label}</label>
                  <p className="px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm whitespace-pre-wrap">
                    {field.display}
                  </p>
                </div>
              ))}
            </div>
          )}

          {articlePhotos.length > 0 && (
            <div>
              <label className="flex items-center text-sm font-semibold text-gray-300 mb-1">
                <FiImage className="mr-2 text-yellow-400" />
                Photos de l&apos;article ({articlePhotos.length})
              </label>
              <PhotoThumbs photos={articlePhotos} onOpen={setPreviewUrl} />
            </div>
          )}

          {rooms.length > 0 && (
            <div>
              <label className="flex items-center text-sm font-semibold text-gray-300 mb-3">
                <FiHome className="mr-2 text-yellow-400" />
                Articles par pièce
              </label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {rooms.map((room, roomIndex) => (
                  <div key={`${room.name}-${roomIndex}`} className="bg-gray-900/50 border border-gray-700 rounded-lg p-4">
                    <p className="text-sm font-semibold text-white mb-3">{room.name}</p>
                    <div className="space-y-3">
                      {room.sizes.map((size) => (
                        <div key={size.key}>
                          <p className="text-xs text-gray-400">
                            {size.label} : <span className="text-white font-medium">{size.count}</span>
                            {size.photos.length > 0 && (
                              <span className="text-gray-500"> · {size.photos.length} photo(s)</span>
                            )}
                          </p>
                          <PhotoThumbs photos={size.photos} onOpen={setPreviewUrl} />
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {history.length > 0 && (
        <div className="mt-6">
          <label className="flex items-center text-sm font-semibold text-gray-300 mb-3">
            <FiClock className="mr-2 text-yellow-400" />
            Historique de la négociation / annulation
          </label>
          <div className="bg-gray-900/50 border border-gray-700 rounded-lg divide-y divide-gray-700">
            {history.map((item) => (
              <div key={item.label} className="flex justify-between gap-4 px-4 py-2 text-sm">
                <span className="text-gray-400">{item.label}</span>
                <span className="text-white text-right">{item.value}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {previewUrl && (
        <div
          className="fixed inset-0 z-[2000] bg-black/80 flex items-center justify-center p-4"
          onClick={() => setPreviewUrl(null)}
        >
          <button
            type="button"
            onClick={() => setPreviewUrl(null)}
            className="absolute top-4 right-4 p-2 rounded-full bg-gray-800 text-white hover:bg-gray-700"
          >
            <FiX className="w-6 h-6" />
          </button>
          <img
            src={previewUrl}
            alt=""
            className="max-w-full max-h-full rounded-lg shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
}
